import React, {useEffect, useMemo, useState} from 'react';
import {useWatch} from 'react-hook-form';

import {toFormDataV2} from 'src/app/forms/engine/formData';
import {ProactForm, ProactFormGrid} from 'src/app/forms/engine/ProactForm';
import {ProactFormView} from 'src/app/forms/engine/ProactFormView';
import {useProactForm} from 'src/app/forms/engine/useProactForm';

import {useAlert} from 'src/shared/hooks/useAlert';
import useActiveFeatureFlags from 'src/shared/hooks/useActiveFeatureFlags';

import {Box} from 'src/design/atoms/Box';
import type {
  PersonnelRequest,
  PersonnelResponse,
} from 'src/api/ProactApiClient';
import {
  AttachmentModuleEnum,
  EntityIdentifierEnum,
  FeatureFlagEnum,
  ModuleEnum,
  NoteModuleEnum,
  RoleEnum,
  TaskAssignmentStateEnum,
  TaskTypeEnum,
  UserTypeEnum,
} from 'src/api/ProactApiClient';
import {SaveButton} from 'src/design/molecules/SaveButton';
import {ProfileAvatar} from 'src/app/personnel/ProfileAvatar';
import {InfoAlert} from 'src/design/molecules/InfoAlert';
import {BackBar} from 'src/app/layout/BackBar';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {SectionCard} from 'src/design/atoms/SectionCard';
import {TaskAssignmentsTable} from 'src/app/tasks/TaskAssignmentsTable';
import {TableBodyRender} from 'src/design/molecules/TableBodyRender';
import {useGetDepartmentSelectOptionsQuery} from 'src/redux/queries/department';
import {
  useCreatePersonMutation,
  useDeletePersonMutation,
  useGetPersonPermissionsQuery,
  useGetPersonQuery,
  useUpdatePersonMutation,
} from 'src/redux/queries/personnel';
import {useGetVendorSelectOptionsQuery} from 'src/redux/queries/vendor';
import {useUrlQueryTabV2} from 'src/shared/hooks/useUrlQueryTabV2';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {normalizeEmail} from 'src/utilities/extensions/stringExtensions';
import type {IFormDataV2} from 'src/types/FormV2';
import {AttachmentsModule} from 'src/app/attachments/AttachmentsModule';
import {UserRelatedAttachmentsModule} from 'src/app/attachments/UserRelatedAttachmentsModule';
import {NotesModule} from 'src/app/notes/NotesModule';
import {NotificationsModule} from 'src/app/notifications/NotificationsModule';
import {
  CreateDefaultPersonnel,
  FormMetaBySections,
  PersonnelPillConfig,
} from '../personnelHelpers';
import {PersonnelAuthenticationTab} from './components/PersonnelAuthenticationTab/PersonnelAuthenticationTab';
import {PersonnelBadgeAccessTab} from './components/PersonnelBadgeAccessTab/PersonnelBadgeAccessTab';

import {useDisclosure} from '@mantine/hooks';
import {IconUserStar} from '@tabler/icons-react';
import {IconSize} from 'src/design/atoms/icons/CommonIcons';
import {ConfirmDialog} from 'src/design/molecules/ConfirmDialog';
import {useAuditLogPermissions} from 'src/shared/auth/permissions/useAuditLogPermissions';
import {usePersonnelPermissions} from 'src/shared/auth/permissions/usePersonnelPermissions';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useExtensionTabs} from 'src/shared/hooks/useExtensionTabs';
import {usePersonnelBreadcrumbs} from 'src/shared/hooks/usePersonnelBreadcrumbs';
import {usePrimaryInstanceLock} from 'src/shared/hooks/usePrimaryInstanceLock';
import type {
  IRouteService,
  ViewModeType,
} from 'src/shared/services/RouterService/IRouteService';
import type {ISelectOption} from 'src/types/Form';
import {AuditModule} from 'src/app/audit-logs/AuditModule';
import {ItemAssignmentsTableByUser} from 'src/app/item-tracker/ItemAssignmentsTableByUser';
import {AdminTaskAssignmentsListByUser} from 'src/app/tasks/AdminTaskAssignmentsListByUser';
import {ConvertUserTypeModal} from '../ConvertUserTypeModal/ConvertUserTypeModal';
import {PersonnelPermissionsTab} from './components/PersonnelPermissionsTab/PersonnelPermissionsTab';
import {PersonnelQRCodeTab} from './components/PersonnelQRCodeTab';
import {PersonnelStatusBanner} from './components/PersonnelStatusBanner';
import {PersonnelTrainingTab} from './components/PersonnelTrainingTab';
import {RelatedFormsTab} from './components/RelatedFormsTab';
import type {PersonnelViewType} from 'src/shared/constants/ViewTypes';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

export interface PersonnelFormProps {
  id?: number;
  userType: UserTypeEnum;
  vendorId?: number;
  mode: ViewModeType;
  view?: PersonnelViewType;
  enableEdit: boolean;
  router: IRouteService;
  fromTab?: 'all' | 'employee' | 'activity';
}

export const PersonnelForm: React.FC<PersonnelFormProps> = ({
  id,
  mode,
  userType,
  vendorId,
  view = 'content',
  enableEdit,
  router,
  fromTab,
}) => {
  const navigate = useAppNavigate();
  const {setAlert} = useAlert();

  const [confirmDeleteOpened, confirmDeleteHandlers] = useDisclosure(false);
  const [convertTypeOpened, convertTypeHandlers] = useDisclosure(false);

  const personnelPerms = usePersonnelPermissions();
  const auditPerms = useAuditLogPermissions();
  const activeFeatureFlags = useActiveFeatureFlags();
  const isBadgePrintingEnabled = activeFeatureFlags.includes(
    FeatureFlagEnum.BadgePrinting
  );
  const extensionTabs = useExtensionTabs(ModuleEnum.Personnel, id ?? 0);

  const {
    data: departments,
    isFetching: isDepartmentFetching,
    isLoading: isDepartmentLoading,
  } = useGetDepartmentSelectOptionsQuery({start: 0, length: 10000});

  const {
    data: vendors,
    isFetching: isVendorFetching,
    isLoading: isVendorLoading,
  } = useGetVendorSelectOptionsQuery({start: 0, length: 10000, active: true});

  const [item, setItem] = useState<PersonnelResponse>();
  const originalVendorIdRef = React.useRef<number | undefined>(undefined);

  const {
    data: personData,
    isLoading: isPersonLoading,
    error: personError,
  } = useGetPersonQuery(id!, {skip: id === undefined || isNaN(+id!)});

  const {data: personPermissions, isLoading: isPermissionsLoading} =
    useGetPersonPermissionsQuery(id!, {
      skip: id === undefined || isNaN(+id!),
    });

  // Only projects the user is assigned to are valid primary-project choices.
  const primaryProjectOptions = useMemo<ISelectOption[]>(
    () =>
      (personPermissions?.projectAccess ?? [])
        .map(({project}) => ({id: project.id, label: project.name}))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [personPermissions]
  );

  const [isUpdating] = useState<boolean>(false);
  const [isSavePending, setIsSavePending] = useState(false);

  const [createPerson] = useCreatePersonMutation();
  const [updatePerson] = useUpdatePersonMutation();
  const [deletePerson] = useDeletePersonMutation();

  const isDataLoading =
    id !== undefined &&
    !isNaN(+id) &&
    (isPersonLoading || isPermissionsLoading);
  const isLoading =
    isDataLoading ||
    isUpdating ||
    isDepartmentFetching ||
    isDepartmentLoading ||
    isVendorFetching ||
    isVendorLoading;

  // Account section is read-only when editing a non-primary instance record;
  // identity is owned by the primary instance and edited from there.
  const primaryInstanceLock = usePrimaryInstanceLock(item);
  const isAccountReadOnly = mode !== 'view' && primaryInstanceLock.isLocked;

  const [vendorChanged, setVendorChanged] = useState(false);

  const sections = useMemo(
    () =>
      FormMetaBySections(
        userType,
        (departments?.data ?? []) as ISelectOption[],
        (vendors?.data ?? []) as ISelectOption[],
        vendorChanged,
        mode === 'create' ? undefined : primaryProjectOptions
      ),
    [userType, departments, vendors, vendorChanged, primaryProjectOptions, mode]
  );

  const defaultFormData = useMemo<IFormDataV2>(() => {
    let state = toFormDataV2({
      ...CreateDefaultPersonnel(),
      userType,
    });
    if (vendorId) {
      state = {
        ...state,
        vendorId,
        roleId: RoleEnum.InstanceUser,
      };
    }
    return state;
  }, [userType, vendorId]);

  const accountForm = useProactForm({
    formMeta: sections.account,
    initialData: defaultFormData,
  });
  const {resetForm: resetAccountForm} = accountForm;

  const employmentForm = useProactForm({
    formMeta: sections.employment,
    initialData: defaultFormData,
  });
  const {resetForm: resetEmploymentForm} = employmentForm;

  const watchedVendorId = useWatch({
    control: employmentForm.methods.control,
    name: 'vendorId',
  });

  useEffect(() => {
    if (userType !== UserTypeEnum.Contractor) {
      return;
    }
    setVendorChanged(
      originalVendorIdRef.current !== undefined &&
        watchedVendorId !== originalVendorIdRef.current
    );
  }, [watchedVendorId, userType]);

  useEffect(() => {
    if (personError) {
      setAlert(parseErrorMessage(personError), 'error');
    }
  }, [personError]);

  useEffect(() => {
    if (personData) {
      setItem(personData);
      originalVendorIdRef.current = personData.vendorId;
      resetAccountForm(toFormDataV2(personData));
      resetEmploymentForm(toFormDataV2(personData));
    }
  }, [personData, resetAccountForm, resetEmploymentForm]);

  useEffect(() => {
    if (isLoading || id !== undefined) {
      return;
    }
    resetAccountForm(defaultFormData);
    resetEmploymentForm(defaultFormData);
  }, [isLoading, id, defaultFormData, resetAccountForm, resetEmploymentForm]);

  const handleSave = async () => {
    try {
      setIsSavePending(true);
      // Both forms are seeded from the full entity, so each holds copies of
      // the other section's fields — merge only each section's own fields
      // over the entity base or a stale copy overwrites the edited value.
      const body = {
        ...(item ?? defaultFormData),
        ...accountForm.getFieldValues(),
        ...employmentForm.getFieldValues(),
      } as PersonnelRequest;
      // Autocomplete clears to ''; drop it so the backend unsets the project.
      body.primaryProjectId = body.primaryProjectId || undefined;
      if (body.email) {
        body.email = normalizeEmail(body.email);
      }

      const res =
        id === undefined
          ? await createPerson(body).unwrap()
          : await updatePerson({id, model: body}).unwrap();

      if (id === undefined && res.existingAccount) {
        setAlert(
          'This email already has an account. A membership was added for this company.',
          'success'
        );
      } else {
        setAlert('Item was successfully updated', 'success');
      }

      navigate(router.getViewRoute(String(res.id)), {replace: true});
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
    setIsSavePending(false);
  };

  const handleValidateAndSave = async () => {
    const accountValid = isAccountReadOnly
      ? true
      : await accountForm.validateForm();
    const employmentValid = await employmentForm.validateForm();

    if (accountValid && employmentValid) {
      void handleSave();
    }
  };

  const handleDelete = async () => {
    confirmDeleteHandlers.close();

    try {
      if (!id) {
        setAlert('ID not found.', 'error');
      }

      setIsSavePending(true);

      await deletePerson(+id!).unwrap();

      setAlert(
        'Item was successfully deleted. This change may take a few minutes to appear.',
        'success'
      );

      navigate(router.getGoBackRoute(String(id)));
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
    setIsSavePending(false);
  };

  const handleItemUpdated = async (updatedItem: PersonnelResponse) => {
    setItem(updatedItem);
    resetAccountForm(toFormDataV2(updatedItem));
    resetEmploymentForm(toFormDataV2(updatedItem));
  };

  const breadcrumbs = usePersonnelBreadcrumbs({
    mode,
    id,
    userType,
    vendorId,
    router,
    itemName: item?.name,
    fromTab,
  });

  const primaryInstanceName = primaryInstanceLock.isLocked
    ? item?.primaryInstanceName
    : undefined;

  const pills = PersonnelPillConfig(
    item?.userType,
    item?.isLeaveOfAbsence,
    item?.isEligible,
    item?.isSuspended,
    item?.badgeExpirationAt,
    primaryInstanceName,
    item?.isBannedOnProperty
  );

  const overviewContent = (
    <>
      {mode !== 'view' ? (
        <>
          {isAccountReadOnly ? (
            <SectionCard title="Account">
              <Box p="md">
                <InfoAlert mb="md">
                  Account information is managed by{' '}
                  {primaryInstanceLock.managedByName}.
                </InfoAlert>
                <ProactForm form={accountForm}>
                  <ProactFormView />
                </ProactForm>
              </Box>
            </SectionCard>
          ) : (
            <SectionCard title="Account">
              <Box p="md">
                <ProactForm form={accountForm}>
                  <ProactFormGrid />
                </ProactForm>
              </Box>
            </SectionCard>
          )}
          <SectionCard
            title="Employment"
            style={{marginTop: 'var(--mantine-spacing-md)'}}>
            <Box p="md">
              <ProactForm form={employmentForm}>
                <ProactFormGrid />
              </ProactForm>
            </Box>
          </SectionCard>
          <SaveButton
            loading={isSavePending}
            onClick={handleValidateAndSave}
            mt="md"
          />
        </>
      ) : (
        <>
          <SectionCard title="Account">
            <Box p="md">
              <ProactForm form={accountForm}>
                <ProactFormView />
              </ProactForm>
            </Box>
          </SectionCard>
          <SectionCard
            title="Employment"
            style={{marginTop: 'var(--mantine-spacing-md)'}}>
            <Box p="md">
              <ProactForm form={employmentForm}>
                <ProactFormView />
              </ProactForm>
            </Box>
          </SectionCard>
        </>
      )}
    </>
  );

  const {RenderedTabsComponent} = useUrlQueryTabV2<PersonnelViewType>({
    view: (view ?? '') as PersonnelViewType,
    defaultView: 'overview',
    onSelectView: selectedView => {
      router.setParameter('view', selectedView);
      navigate(router.getViewRoute(String(id)), {replace: true});
    },
    tabs: [
      {
        view: 'overview',
        label: 'Overview',
        component: overviewContent,
      },
      {
        view: 'item',
        label: 'Item Tracker',
        disabled: mode !== 'view',
        component: (
          <>
            {item && (
              <ItemAssignmentsTableByUser
                assignedToUserId={item.id}
                assignedToUserLabel={item.name}
              />
            )}
          </>
        ),
      },
      {
        view: 'actions',
        label: 'Actions',
        disabled: mode !== 'view',
        component: (
          <>
            {item && (
              <TaskAssignmentsTable
                showForUserId={item.id}
                taskType={TaskTypeEnum.Actions}
                defaultStates={[
                  TaskAssignmentStateEnum.Assigned,
                  TaskAssignmentStateEnum.Submitted,
                  TaskAssignmentStateEnum.Viewed,
                ]}
              />
            )}
          </>
        ),
      },
      {
        view: 'assignedforms',
        label: 'Assigned Forms',
        disabled: mode !== 'view',
        component: (
          <>
            {item && (
              <TaskAssignmentsTable
                showForUserId={item.id}
                taskType={TaskTypeEnum.Form}
                defaultStates={[
                  TaskAssignmentStateEnum.Assigned,
                  TaskAssignmentStateEnum.Submitted,
                  TaskAssignmentStateEnum.Viewed,
                ]}
              />
            )}
          </>
        ),
      },
      {
        view: 'toolboxtalks',
        label: 'Toolbox Talks',
        disabled: mode !== 'view',
        component: (
          <>
            {item && (
              <AdminTaskAssignmentsListByUser
                userId={item.id}
                taskType={TaskTypeEnum.ToolboxTalk}
                module={ModuleEnum.ToolboxTalks}
              />
            )}
          </>
        ),
      },
      {
        view: 'relatedforms',
        label: 'Related Forms',
        disabled: mode !== 'view' || !personnelPerms.relatedForms,
        component: <>{item && <RelatedFormsTab item={item} />}</>,
      },
      {
        view: 'training',
        label: 'Training',
        disabled: mode !== 'view',
        component: <>{item && <PersonnelTrainingTab item={item} />}</>,
      },
      {
        view: 'notes',
        label: 'Notes',
        disabled: mode !== 'view' || !personnelPerms.notes,
        component: (
          <>
            {item && (
              <NotesModule noteType={NoteModuleEnum.User} entityId={item.id} />
            )}
          </>
        ),
      },
      {
        view: 'attachments',
        label: 'Attachments',
        disabled: mode !== 'view' || !personnelPerms.attachments,
        component: (
          <>
            {item && (
              <AttachmentsModule
                attachmentType={AttachmentModuleEnum.User}
                entityId={item.id}
              />
            )}
          </>
        ),
      },
      {
        view: 'related-attachments',
        label: 'Related Attachments',
        disabled: mode !== 'view' || !personnelPerms.attachments,
        component: item ? (
          <UserRelatedAttachmentsModule userId={item.id} />
        ) : null,
      },
      {
        view: 'notifications',
        label: 'Notifications',
        disabled: mode !== 'view' || !personnelPerms.notifications,
        component: <>{item && <NotificationsModule recipientId={item.id} />}</>,
      },
      {
        view: 'badge-access',
        label: 'Badge',
        hidden:
          !(
            item?.userType === UserTypeEnum.Contractor &&
            (personnelPerms.badgeAccess || personnelPerms.badgeAccessAdmin)
          ) && !(isBadgePrintingEnabled && personnelPerms.printBadge),
        disabled: mode !== 'view',
        component: (
          <PersonnelBadgeAccessTab
            item={item}
            onItemUpdated={handleItemUpdated}
          />
        ),
      },
      {
        view: 'qr-code',
        label: 'QR Code',
        disabled: mode !== 'view' || !personnelPerms.qrCode,
        component: <>{item && <PersonnelQRCodeTab item={item} />}</>,
      },
      {
        view: 'authentication',
        label: 'Authentication',
        disabled: mode !== 'view' || !personnelPerms.auth,
        component: (
          <PersonnelAuthenticationTab
            item={item}
            onItemUpdated={handleItemUpdated}
          />
        ),
      },
      {
        view: 'permissions',
        label: 'Permissions',
        disabled: mode !== 'view' || !personnelPerms.perms,
        component: <PersonnelPermissionsTab item={item} />,
      },
      {
        view: 'audit',
        label: 'Audit Log',
        disabled: mode !== 'view' || !auditPerms.view,
        component: (
          <>
            {item && (
              <AuditModule
                entityType={EntityIdentifierEnum.User}
                entityId={String(item.id)}
              />
            )}
          </>
        ),
      },
      ...extensionTabs,
    ],
  });

  return isLoading === false ? (
    <ContentWrapper>
      <BackBar
        fallbackTo={router.getGoBackRoute(String(id))}
        breadcrumbs={breadcrumbs}
      />
      <PageHeader>
        <PageHeader.Title
          subtitle={item?.email}
          icon={
            <ProfileAvatar
              alt={item?.name ?? 'Avatar Image'}
              src={item?.avatarUrl}
            />
          }>
          {mode === 'create'
            ? `New ${
                userType === UserTypeEnum.Employee ? 'Employee' : 'Contractor'
              }`
            : item?.name}
        </PageHeader.Title>

        {pills.length > 0 && (
          <PageHeader.Badges>
            {pills.map((pill, index) => (
              <PageHeader.Badge key={index} color={pill.color} variant="light">
                {pill.text}
              </PageHeader.Badge>
            ))}
          </PageHeader.Badges>
        )}

        <PageHeader.Actions>
          {mode === 'view' && personnelPerms.convertUserType && item && (
            <PageHeader.Action
              variant="edit"
              icon={<IconUserStar size={IconSize.sm} />}
              onClick={() => convertTypeHandlers.open()}>
              {item.userType === UserTypeEnum.Employee
                ? 'Convert to Contractor'
                : 'Convert to Employee'}
            </PageHeader.Action>
          )}
          {mode === 'view' && enableEdit && personnelPerms.update && (
            <PageHeader.Action
              variant="edit"
              onClick={() => navigate(router.getEditRoute(String(id)))}
            />
          )}
          {mode === 'view' && personnelPerms.delete && (
            <PageHeader.Action
              variant="delete"
              onClick={() => confirmDeleteHandlers.open()}
            />
          )}
          {mode !== 'view' && (
            <PageHeader.Action
              variant="save"
              loading={isSavePending}
              onClick={handleValidateAndSave}
            />
          )}
        </PageHeader.Actions>
      </PageHeader>

      {item && <PersonnelStatusBanner item={item} />}

      {RenderedTabsComponent}

      <ConfirmDialog
        opened={confirmDeleteOpened}
        onClose={() => confirmDeleteHandlers.close()}
        onConfirm={handleDelete}
        confirmationButtonColor="red"
        title="Are you sure you want to delete this user?"
        body="This action cannot be undone. All of their data will be deleted alongside their account."
      />
      <ConvertUserTypeModal
        opened={convertTypeOpened}
        onClose={convertTypeHandlers.close}
        item={item}
        onSuccess={response => {
          if (
            response.userType === UserTypeEnum.Contractor &&
            response.vendorId
          ) {
            navigate(
              PathUtility.VendorPersonnelView(
                response.id,
                response.vendorId,
                'overview'
              )
            );
          } else {
            navigate(PathUtility.PersonnelView(response.id, 'overview'));
          }
        }}
      />
    </ContentWrapper>
  ) : (
    <TableBodyRender isLoading={isLoading} />
  );
};
